// UTM Parameter Tracking Utility
import { hasAnalyticsConsent } from './consent';

const UTM_PARAMS = [
  'utm_source',
  'utm_medium', 
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id'
];

/**
 * Extract UTM parameters from URL and store them
 * @param {string} url - The URL to extract parameters from (defaults to current URL)
 */
export const trackUTMParams = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const utmData = {};
    let hasUTMParams = false;

    // Extract UTM parameters
    UTM_PARAMS.forEach(param => {
      const value = urlObj.searchParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUTMParams = true;
      }
    });

    // If we found UTM parameters, store them
    if (hasUTMParams) {
      // Store in localStorage for persistence
      localStorage.setItem('utm_params', JSON.stringify(utmData));
      
      // Also store timestamp for when UTM was captured
      localStorage.setItem('utm_timestamp', Date.now().toString());
      
      // Store referrer information
      if (document.referrer) {
        localStorage.setItem('utm_referrer', document.referrer);
      }

      // Send UTM parameters to Google Analytics
      if (hasAnalyticsConsent() && typeof window !== 'undefined' && window.gtag) {
        const utmProps = {
          utm_source: utmData.utm_source,
          utm_medium: utmData.utm_medium,
          utm_campaign: utmData.utm_campaign,
          utm_term: utmData.utm_term,
          utm_content: utmData.utm_content,
          utm_id: utmData.utm_id
        };

        Object.keys(utmProps).forEach((key) => {
          if (!utmProps[key]) {
            delete utmProps[key];
          }
        });

        // Record a GA4 event so the capture shows in reporting
        window.gtag('event', 'utm_capture', utmProps);

        // Persist the UTM values as GA4 user properties for downstream attribution work
        window.gtag('set', 'user_properties', utmProps);
      }

      console.log('UTM parameters captured and sent to GA:', utmData);
      return utmData;
    }

    return null;
  } catch (error) {
    console.error('Error tracking UTM parameters:', error);
    return null;
  }
};

/**
 * Get stored UTM parameters
 * @returns {Object|null} Stored UTM data or null if none exists
 */
export const getStoredUTMParams = () => {
  try {
    const stored = localStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error retrieving UTM parameters:', error);
    return null;
  }
};

/**
 * Get UTM timestamp
 * @returns {number|null} Timestamp when UTM was captured
 */
export const getUTMTimestamp = () => {
  try {
    const timestamp = localStorage.getItem('utm_timestamp');
    return timestamp ? parseInt(timestamp) : null;
  } catch (error) {
    console.error('Error retrieving UTM timestamp:', error);
    return null;
  }
};

/**
 * Get stored referrer
 * @returns {string|null} Stored referrer URL
 */
export const getStoredReferrer = () => {
  try {
    return localStorage.getItem('utm_referrer');
  } catch (error) {
    console.error('Error retrieving referrer:', error);
    return null;
  }
};

/**
 * Clear stored UTM data
 */
export const clearUTMData = () => {
  try {
    localStorage.removeItem('utm_params');
    localStorage.removeItem('utm_timestamp');
    localStorage.removeItem('utm_referrer');
  } catch (error) {
    console.error('Error clearing UTM data:', error);
  }
};

/**
 * Get all UTM tracking data
 * @returns {Object} Complete UTM tracking data
 */
export const getAllUTMData = () => {
  return {
    utmParams: getStoredUTMParams(),
    timestamp: getUTMTimestamp(),
    referrer: getStoredReferrer(),
    currentUrl: window.location.href
  };
};

/**
 * Debug function to log current UTM data
 */
export const debugUTMData = () => {
  const data = getAllUTMData();
  console.log('Current UTM Data:', data);
  return data;
}; 
